import React from "react";
import { useLanguage } from "@/context/LanguageContext";
import { ArrowRight, ArrowDown } from "lucide-react";

const HERO_IMG =
  "https://images.pexels.com/photos/30554306/pexels-photo-30554306.jpeg?auto=compress&cs=tinysrgb&w=2000";

export const Hero = () => {
  const { t, lang } = useLanguage();
  const Arrow = lang === "ar" ? ArrowRight : ArrowRight;

  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section
      id="home"
      data-testid="hero-section"
      className="relative min-h-screen w-full overflow-hidden grain"
    >
      {/* Background */}
      <div className="absolute inset-0">
        <img
          src={HERO_IMG}
          alt="Dubai skyline at dusk"
          className="w-full h-full object-cover"
          loading="eager"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#0A0A0A]/70 via-[#0A0A0A]/55 to-[#0A0A0A]" />
        <div
          className={`absolute inset-0 ${
            lang === "ar"
              ? "bg-gradient-to-l from-[#0A0A0A] via-[#0A0A0A]/40 to-transparent"
              : "bg-gradient-to-r from-[#0A0A0A] via-[#0A0A0A]/40 to-transparent"
          }`}
        />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 pt-40 pb-24 min-h-screen flex flex-col justify-center">
        <div className="max-w-3xl reveal">
          <div
            data-testid="hero-eyebrow"
            className="eyebrow mb-8 flex items-center gap-3"
          >
            <span className="w-8 h-px bg-[#D4AF37]" />
            <span>{t.hero.eyebrow}</span>
          </div>

          <h1
            data-testid="hero-title"
            className="font-serif-display text-[#F8F8F8] text-5xl sm:text-6xl md:text-7xl lg:text-[88px] leading-[1.02] tracking-tight"
          >
            <span className="block font-light italic text-[#C2A878]">
              {t.hero.title_1}
            </span>
            <span className="block font-medium">{t.hero.title_2}</span>
            <span className="block font-light text-[#A1A1AA]">
              {t.hero.title_3}
            </span>
          </h1>

          <p
            data-testid="hero-subtitle"
            className="mt-8 text-base md:text-lg text-[#A1A1AA] max-w-xl font-light leading-relaxed"
          >
            {t.hero.subtitle}
          </p>

          <div className="mt-12 flex flex-wrap items-center gap-4">
            <button
              data-testid="hero-cta-primary"
              onClick={() => scrollTo("portfolio")}
              className="group inline-flex items-center gap-3 bg-[#D4AF37] text-[#0A0A0A] hover:bg-[#E6C657] transition-colors duration-300 px-8 py-4 text-sm uppercase tracking-[0.2em] font-medium"
            >
              <span>{t.hero.cta_primary}</span>
              <Arrow
                size={16}
                className={`${
                  lang === "ar" ? "rotate-180" : ""
                } group-hover:translate-x-1 transition-transform`}
              />
            </button>
            <button
              data-testid="hero-cta-secondary"
              onClick={() => scrollTo("contact")}
              className="inline-flex items-center gap-3 border border-[#D4AF37] text-[#D4AF37] hover:bg-[#D4AF37] hover:text-[#0A0A0A] transition-colors duration-300 px-8 py-4 text-sm uppercase tracking-[0.2em] font-medium"
            >
              {t.hero.cta_secondary}
            </button>
          </div>
        </div>

        {/* Scroll cue */}
        <div className="absolute bottom-10 inset-x-0 flex justify-center">
          <div className="flex flex-col items-center gap-3 text-[#A1A1AA]">
            <span className="text-[10px] uppercase tracking-[0.3em]">
              {t.hero.scroll}
            </span>
            <ArrowDown size={16} className="animate-bounce text-[#D4AF37]" />
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
